import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';
import {
    MetaAdAttribution,
    UnmappedCtwaSummary,
    createMetaAdAttributionRequest,
    deleteMetaAdAttributionRequest,
    fetchMetaAdAttributionsRequest,
    fetchUnmappedCtwaRequest,
    updateMetaAdAttributionRequest,
} from '../api/metaAdAttributionsApi';
import { ReferralLink, fetchReferralLinksRequest } from '../api/referralLinksApi';
import { formatDistanceToNow } from 'date-fns';

type SnackbarState = { open: boolean; message: string; severity: 'success' | 'error' | 'info' };

const emptyForm = {
    ctwaClid: '',
    adId: '',
    campaignId: '',
    referralLinkId: '',
    notes: '',
};

const MetaAdAttributionsPage: React.FC = () => {
    const [tab, setTab] = useState<'mappings' | 'unmapped'>('mappings');
    const [mappings, setMappings] = useState<MetaAdAttribution[]>([]);
    const [unmapped, setUnmapped] = useState<UnmappedCtwaSummary[]>([]);
    const [referralLinks, setReferralLinks] = useState<ReferralLink[]>([]);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState({ ...emptyForm });
    const [submitting, setSubmitting] = useState(false);

    const loadAll = useCallback(async () => {
        try {
            setLoading(true);
            const [mappingList, unmappedList, links] = await Promise.all([
                fetchMetaAdAttributionsRequest(),
                fetchUnmappedCtwaRequest(),
                fetchReferralLinksRequest(),
            ]);
            setMappings(mappingList);
            setUnmapped(unmappedList);
            setReferralLinks(links);
        } catch (err) {
            console.error(err);
            setSnackbar({ open: true, message: 'Failed to load attribution mappings', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const linkOptions = useMemo(() => {
        // Active links float to the top; expired/inactive sink so admins don't
        // accidentally credit a stale campaign. Deliberately not filtered out —
        // admins may need to map historical attribution.
        return [...referralLinks].sort((a, b) => {
            const aActive = a.isActive ? 1 : 0;
            const bActive = b.isActive ? 1 : 0;
            return bActive - aActive;
        });
    }, [referralLinks]);

    const linkLabel = (link: ReferralLink) => {
        const status = link.isActive ? '' : ' [inactive]';
        return `${link.title} — ${link.code}${status}`;
    };

    const openCreate = (prefill?: Partial<typeof emptyForm>) => {
        setEditingId(null);
        setForm({ ...emptyForm, ...prefill });
        setDialogOpen(true);
    };

    const openEdit = (mapping: MetaAdAttribution) => {
        setEditingId(mapping.id);
        setForm({
            ctwaClid: mapping.ctwaClid || '',
            adId: mapping.adId || '',
            campaignId: mapping.campaignId || '',
            referralLinkId: mapping.referralLinkId || '',
            notes: mapping.notes || '',
        });
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            const payload = {
                ctwaClid: form.ctwaClid.trim() || null,
                adId: form.adId.trim() || null,
                campaignId: form.campaignId.trim() || null,
                referralLinkId: form.referralLinkId || null,
                notes: form.notes.trim() || null,
            };

            if (!editingId && !payload.ctwaClid && !payload.adId && !payload.campaignId) {
                setSnackbar({
                    open: true,
                    message: 'Provide at least one of ctwa_clid, ad_id, or campaign_id',
                    severity: 'error',
                });
                return;
            }

            const result = editingId
                ? await updateMetaAdAttributionRequest({
                      id: editingId,
                      referralLinkId: payload.referralLinkId,
                      notes: payload.notes,
                  })
                : await createMetaAdAttributionRequest(payload);

            const verb = editingId ? 'Updated' : 'Created';
            const backfillNote = result.backfilledSessionCount
                ? ` — ${result.backfilledSessionCount} session${result.backfilledSessionCount === 1 ? '' : 's'} retroactively attributed`
                : '';
            setSnackbar({
                open: true,
                message: `${verb} mapping${backfillNote}`,
                severity: result.backfilledSessionCount > 0 ? 'success' : 'info',
            });
            setDialogOpen(false);
            await loadAll();
        } catch (err: any) {
            const message = err.response?.data?.message || err.message || 'Failed to save mapping';
            setSnackbar({ open: true, message: String(message), severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (mapping: MetaAdAttribution) => {
        const confirmMsg = mapping.referralLinkTitle
            ? `Delete mapping → "${mapping.referralLinkTitle}"? Existing attributed sessions stay attributed.`
            : 'Delete this mapping?';
        if (!window.confirm(confirmMsg)) return;
        try {
            await deleteMetaAdAttributionRequest(mapping.id);
            setSnackbar({ open: true, message: 'Mapping deleted', severity: 'success' });
            await loadAll();
        } catch (err) {
            console.error(err);
            setSnackbar({ open: true, message: 'Failed to delete mapping', severity: 'error' });
        }
    };

    const mappingColumns: GridColDef[] = [
        {
            field: 'ctwaClid',
            headerName: 'CTWA clid',
            flex: 1,
            minWidth: 180,
            renderCell: (p: GridRenderCellParams) => p.value ? (
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.value}</Typography>
            ) : <Typography variant="caption" color="text.secondary">-</Typography>,
        },
        {
            field: 'adId',
            headerName: 'ad_id',
            width: 160,
            renderCell: (p: GridRenderCellParams) => p.value ? (
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.value}</Typography>
            ) : <Typography variant="caption" color="text.secondary">-</Typography>,
        },
        {
            field: 'campaignId',
            headerName: 'campaign_id',
            width: 160,
            renderCell: (p: GridRenderCellParams) => p.value ? (
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.value}</Typography>
            ) : <Typography variant="caption" color="text.secondary">-</Typography>,
        },
        {
            field: 'referralLinkTitle',
            headerName: 'Credited link',
            flex: 1,
            minWidth: 220,
            renderCell: (p: GridRenderCellParams) => {
                const m = p.row as MetaAdAttribution;
                if (!m.referralLinkId) {
                    return <Chip label="ack-only" size="small" variant="outlined" />;
                }
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinkIcon fontSize="small" color="action" />
                        <Box>
                            <Typography variant="body2">{m.referralLinkTitle}</Typography>
                            {m.referralLinkCode && (
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                    {m.referralLinkCode}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            },
        },
        {
            field: 'notes',
            headerName: 'Notes',
            flex: 1,
            minWidth: 180,
            renderCell: (p: GridRenderCellParams) => p.value || <Typography variant="caption" color="text.secondary">-</Typography>,
        },
        {
            field: 'createdAt',
            headerName: 'Created',
            width: 130,
            renderCell: (p: GridRenderCellParams) =>
                p.value ? formatDistanceToNow(new Date(p.value), { addSuffix: true }) : '-',
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 110,
            sortable: false,
            renderCell: (p: GridRenderCellParams) => {
                const m = p.row as MetaAdAttribution;
                return (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(m)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDelete(m)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            },
        },
    ];

    const unmappedColumns: GridColDef[] = [
        {
            field: 'ctwaClid',
            headerName: 'CTWA clid',
            flex: 1,
            minWidth: 200,
            renderCell: (p: GridRenderCellParams) => (
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.value}</Typography>
            ),
        },
        {
            field: 'adId',
            headerName: 'ad_id',
            width: 160,
            renderCell: (p: GridRenderCellParams) => p.value || <Typography variant="caption" color="text.secondary">-</Typography>,
        },
        {
            field: 'campaignId',
            headerName: 'campaign_id',
            width: 160,
            renderCell: (p: GridRenderCellParams) => p.value || <Typography variant="caption" color="text.secondary">-</Typography>,
        },
        {
            field: 'sessionCount',
            headerName: 'Sessions',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p: GridRenderCellParams) => (
                <Chip label={p.value} size="small" color={p.value > 1 ? 'warning' : 'default'} />
            ),
        },
        {
            field: 'lastSeenAt',
            headerName: 'Last seen',
            width: 150,
            renderCell: (p: GridRenderCellParams) =>
                p.value ? formatDistanceToNow(new Date(p.value), { addSuffix: true }) : '-',
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 140,
            sortable: false,
            renderCell: (p: GridRenderCellParams) => {
                const row = p.row as UnmappedCtwaSummary;
                return (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() =>
                            openCreate({
                                ctwaClid: row.ctwaClid,
                                adId: row.adId || '',
                                campaignId: row.campaignId || '',
                            })
                        }
                    >
                        Map
                    </Button>
                );
            },
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                        <Typography variant="h5" component="h1">
                            Meta Ad Attributions
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Map Click-to-WhatsApp Ad signals (ctwa_clid, ad_id, campaign_id) to a recruiter's referral link.
                            New mappings retroactively attribute matching past sessions.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Refresh">
                            <IconButton onClick={loadAll} disabled={loading}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCreate()}>
                            New Mapping
                        </Button>
                    </Box>
                </Box>

                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                    <Tab value="mappings" label={`Mappings (${mappings.length})`} />
                    <Tab
                        value="unmapped"
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                Unmapped
                                {unmapped.length > 0 && (
                                    <Chip
                                        label={unmapped.length}
                                        size="small"
                                        color="warning"
                                        sx={{ height: 18, fontSize: 11 }}
                                    />
                                )}
                            </Box>
                        }
                    />
                </Tabs>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : tab === 'mappings' ? (
                    mappings.length === 0 ? (
                        <Alert severity="info">
                            No mappings yet. Click "New Mapping" or visit the Unmapped tab to start attributing.
                        </Alert>
                    ) : (
                        <DataGrid
                            rows={mappings}
                            columns={mappingColumns}
                            getRowId={(row) => row.id}
                            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                            pageSizeOptions={[10, 25, 50]}
                            autoHeight
                            disableRowSelectionOnClick
                        />
                    )
                ) : unmapped.length === 0 ? (
                    <Alert severity="success">
                        No unmapped CTWA signals — every captured ad has a mapping (or has been attributed downstream).
                    </Alert>
                ) : (
                    <DataGrid
                        rows={unmapped}
                        columns={unmappedColumns}
                        getRowId={(row) => row.ctwaClid}
                        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                        disableRowSelectionOnClick
                    />
                )}
            </Paper>

            <Dialog open={dialogOpen} onClose={() => !submitting && setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingId ? 'Edit mapping' : 'New mapping'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="ctwa_clid (most specific)"
                            value={form.ctwaClid}
                            onChange={(e) => setForm({ ...form, ctwaClid: e.target.value })}
                            disabled={!!editingId}
                            helperText={editingId ? 'Identifier fields are locked after create' : 'Best for one-off click identifiers'}
                            fullWidth
                        />
                        <TextField
                            label="ad_id"
                            value={form.adId}
                            onChange={(e) => setForm({ ...form, adId: e.target.value })}
                            disabled={!!editingId}
                            helperText={editingId ? '' : 'Use when you want every click on this ad to credit the same link'}
                            fullWidth
                        />
                        <TextField
                            label="campaign_id"
                            value={form.campaignId}
                            onChange={(e) => setForm({ ...form, campaignId: e.target.value })}
                            disabled={!!editingId}
                            helperText={editingId ? '' : 'Broadest match — every ad in the campaign'}
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel>Credit which referral link?</InputLabel>
                            <Select
                                value={form.referralLinkId}
                                onChange={(e) => setForm({ ...form, referralLinkId: e.target.value })}
                                label="Credit which referral link?"
                            >
                                <MenuItem value="">
                                    <em>Ack-only (no credit)</em>
                                </MenuItem>
                                {linkOptions.map((l) => (
                                    <MenuItem key={l.id} value={l.id} disabled={!l.isActive}>
                                        {linkLabel(l)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Notes (optional)"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            multiline
                            rows={2}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
                        {submitting ? <CircularProgress size={20} /> : editingId ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default MetaAdAttributionsPage;
